import InfoIcon from "@mui/icons-material/Info";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import {
  Container,
  Grid,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { Box, Stack } from "@mui/system";
import { useBackofficeConstant } from "components/context/ConstantProvider";
import useQueryStringState from "components/hooks/useQueryStringState";
import { InfoButton } from "components/ui/customs/components/ModelInfoButton";
import CustomSimpleTable from "components/ui/customs/table/CustomSimpleTable";
import useCustomDataSource from "components/ui/customs/useCustomDataSource";
import dayjs from "dayjs";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import { useEffect, useState } from "react";
import FinancialsImportWidget from "../modelComponents/FinancialsImportWidget";
import { useTranslation } from "react-i18next";
dayjs.extend(quarterOfYear);

const FinancialCell = ({ value }) => {
  return (
    <Typography
      variant="body2"
      sx={{ textAlign: "right" }}
      color={
        value && value.proposed_value !== null
          ? "secondary.main"
          : value && value.status == "CALCULATED"
          ? "darkgreen"
          : "primary"
      }
    >
      {value && value.value !== null
        ? value.value?.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        : "-"}
      {value && value.proposed_value !== null && (
        <Tooltip
          title={`Proposed Value: ${value.proposed_value.toLocaleString(
            undefined,
            {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            },
          )}`}
        >
          <InfoIcon sx={{ verticalAlign: "middle", ml: 0.5 }} />
        </Tooltip>
      )}
    </Typography>
  );
};

const RowTitleCell = ({ value }) => {
  return (
    <Tooltip title={value.name_unit_str}>
      <span>{value.name_unit_str}</span>
    </Tooltip>
  );
};

export const useDealFinancialsHelpers = () => {
  const { t } = useTranslation("translation");
  const nextLastDayOfPeriod = (date, closing_month = 12, period) => {
    let newDate = dayjs(date);
    if (period === "MONTH") {
      newDate = newDate.add(1, "day").endOf("month");
    } else if (period === "QUARTER") {
      newDate = newDate.add(1, "day").endOf("quarter");
    } else if (period === "YEAR" || period === "YEAR_TO_DATE") {
      if (newDate.add(1, "day").month() + 1 > closing_month) {
        newDate = newDate
          .add(1, "day")
          .endOf("year")
          .add(closing_month, "month")
          .endOf("month");
      } else {
        newDate = newDate
          .add(1, "day")
          .startOf("year")
          .add(closing_month - 1, "month")
          .endOf("month");
      }
    }
    return newDate;
  };

  const previousLastDayOfPeriod = (date, closing_month = 12, period) => {
    let newDate = dayjs(date);
    if (period === "MONTH") {
      newDate = newDate.startOf("month").add(-1, "day");
    } else if (period === "QUARTER") {
      newDate = newDate.startOf("quarter").add(-1, "day");
    } else if (period === "YEAR" || period === "YEAR_TO_DATE") {
      if (newDate.month() + 1 > closing_month) {
        newDate = newDate
          .startOf("year")
          .add(closing_month - 1, "month")
          .endOf("month");
      } else {
        newDate = newDate
          .startOf("year")
          .add(closing_month - 13, "month")
          .endOf("month");
      }
    }
    return newDate;
  };

  const getColumnLabel = ({ date, closing_month, period, category }) => {
    let categoryLabel = {
      ACTUAL: "",
      BUSINESS_PLAN: " - " + t("Forecast"),
      BUDGET: " - " + t("Budget"),
    }[category];
    categoryLabel =
      categoryLabel === undefined ? ` - ${t("Unknown")}` : categoryLabel;

    if (period === "MONTH") {
      return {
        label: dayjs(date).format("MM/YYYY"),
        subtitle: `${t("Month")}${categoryLabel}`,
      };
    } else if (period === "YEAR_TO_DATE") {
      const ytd_month = ((dayjs(date).month() - closing_month + 12) % 12) + 1;
      return {
        label: dayjs(date).format("MM/YYYY"),
        subtitle: `YTD (${ytd_month} months)${categoryLabel}`,
      };
    } else if (period === "YEAR") {
      let d = dayjs(date);
      return {
        label:
          closing_month !== 12
            ? `${d.add(-1, "year").format("YYYY")}/${d.format("YY")}`
            : d.format("YYYY"),
        subtitle: t("Full year") + categoryLabel,
      };
    } else if (period === "QUARTER") {
      return {
        label: `Q${dayjs(date).quarter()}-${dayjs(date).format("YYYY")}`,
        subtitle: t("Quarter") + categoryLabel,
      };
    }

    return { label: `Error for period = ${period}`, subtitle: "" };
  };

  const getColumns = (period, date, deal) => {
    let columns = [
      {
        field: "type",
        headerName: "Name",
        width: 150,
        display: "flex",
        renderCell: RowTitleCell,
        headerClassName: "custom--header",
      },
    ];

    const periods = [];
    let current = dayjs(date);

    if (period === "MONTH" || period === "YEAR_TO_DATE") {
      for (let i = 11; i >= 0; i--) {
        const d = current.subtract(i, "month");
        periods.push({
          label: getColumnLabel({
            date: d,
            closing_month: deal?.closing_month,
            period,
          }).label,
          value: { month: d.month(), year: d.year(), period },
          key: d.format("YYYY-MM"),
        });
      }
    } else if (period === "YEAR") {
      for (let i = 11; i >= 0; i--) {
        const d = current.subtract(i, "year");
        periods.push({
          label: getColumnLabel({
            date: d,
            closing_month: deal?.closing_month,
            period,
          }).label,
          value: { month: d.month(), year: d.year(), period },
          key: d.format("YYYY"),
        });
      }
    } else if (period === "QUARTER") {
      for (let i = 11; i >= 0; i--) {
        const d = current.subtract(i, "quarter");
        periods.push({
          label: getColumnLabel({
            date: d,
            closing_month: deal?.closing_month,
            period,
          }).label,
          value: { month: d.month(), year: d.year(), period },
          key: `${d.format("YYYY")}-Q${d.quarter()}`,
        });
      }
    }

    columns = columns.concat(
      periods.map((p) => ({
        field: p.key,
        headerName: p.label,
        width: 120,
        type: "number",
        _meta: p.value,
        width: 80,
        display: "flex",
        renderCell: FinancialCell,
        headerClassName: "custom--header",
      })),
    );

    return columns;
  };

  const getRows = (
    financial_data_types,
    financial_data_vals,
    columns,
    category,
  ) => {
    if (!financial_data_types || !financial_data_vals || !columns) return [];

    return financial_data_types.map((type) => {
      const row = {
        id: type.id,
        type: type,
      };

      // For each column (except the first 'name' column), fill value
      columns.slice(1).forEach((col) => {
        const meta = col._meta;
        // Determine period key type
        // For YEAR: col.field = '2017', for MONTH: '2017-12', for QUARTER: '2017-Q4'
        const matchFn = (v) => {
          return (
            v.financial_data_type === type.id &&
            v.category === category &&
            v.year === meta.year &&
            v.month === meta.month + 1 &&
            v.period === meta.period
          );
        };
        const valObj = financial_data_vals.find(matchFn);

        row[col.field] = valObj ?? null;
      });

      return row;
    });
  };

  function toClosingMonthStr(month) {
    if (!month) return "";
    // Convert month number to string, e.g. 12 => "December"
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return months[month - 1] || month;
  }

  const getTableSubtitle = (closing_month) => {
    return (
      t("Closing date is end of") + " " + t(toClosingMonthStr(closing_month))
    );
  };

  return {
    nextLastDayOfPeriod,
    previousLastDayOfPeriod,
    getColumnLabel,
    getColumns,
    getRows,
    toClosingMonthStr,
    getTableSubtitle,
  };
};

const DealDataTable = ({
  financial_data_vals,
  financial_data_types,
  deal,
  onStateChange,
  state = {
    category: "ACTUAL",
    period: "MONTH",
    date: lastDayOfMonth(new Date()),
  },
}) => {
  const {
    nextLastDayOfPeriod,
    previousLastDayOfPeriod,
    getColumns,
    getRows,
    getTableSubtitle,
  } = useDealFinancialsHelpers();
  const dataCategories = useBackofficeConstant("financials").dataCategories;
  const dataPeriods = useBackofficeConstant("financials").dataPeriods;

  const [category, setCategory] = useState(state.category);
  const [period, setPeriod] = useState(state.period);
  const [date, setDate] = useState(state.date);

  const handleDateChange = (command) => {
    setDate((previousDate) => {
      let newDate = dayjs(previousDate);
      if (command === "+") {
        newDate = nextLastDayOfPeriod(newDate, deal?.closing_month, "MONTH");
      } else if (command === "-") {
        newDate = previousLastDayOfPeriod(
          newDate,
          deal?.closing_month,
          "MONTH",
        );
      } else if (command === "++") {
        newDate = nextLastDayOfPeriod(
          newDate,
          deal?.closing_month,
          period === "MONTH" ? "YEAR" : period,
        );
      } else if (command === "--") {
        newDate = previousLastDayOfPeriod(
          newDate,
          deal?.closing_month,
          period === "MONTH" ? "YEAR" : period,
        );
      }
      return newDate.endOf("month").toDate();
    });
  };

  const handleSetPeriod = (newPeriod) => {
    setPeriod(newPeriod);
    setDate((date) => {
      const newDate = previousLastDayOfPeriod(
        date,
        deal?.closing_month,
        newPeriod,
      );
      return nextLastDayOfPeriod(newDate, deal?.closing_month, newPeriod);
    });
  };

  useEffect(() => {
    onStateChange({ category, period, date });
  }, [category, period, date, onStateChange]);

  const subtitle = getTableSubtitle(deal?.closing_month);

  return (
    <>
      <Grid
        container
        spacing={1}
        justifyContent="space-between"
        py={1}
        width={1}
      >
        <Grid>
          <ToggleButtonGroup
            value={category}
            exclusive
            onChange={(e) => setCategory(e.target.value)}
            aria-label="Category"
          >
            {dataCategories.map((cat) => (
              <ToggleButton size="small" key={cat.value} value={cat.value}>
                {cat.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Grid>
        <Grid>
          <ToggleButtonGroup
            value={period}
            exclusive
            onChange={(e) => handleSetPeriod(e.target.value)}
            aria-label="Period"
          >
            {dataPeriods.map((p) => (
              <ToggleButton size="small" key={p.value} value={p.value}>
                {p.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Grid>
        <Grid>
          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton onClick={() => handleDateChange("--")}>
              <KeyboardDoubleArrowLeftIcon />
            </IconButton>
            <IconButton
              onClick={() => handleDateChange("-")}
              disabled={period !== "MONTH"}
            >
              <KeyboardArrowLeftIcon />
            </IconButton>
            <Typography component="span" variant="body1" sx={{ lineHeight: 1 }}>
              {new Date(date).toLocaleDateString()}
            </Typography>
            <IconButton
              onClick={() => handleDateChange("+")}
              disabled={period !== "MONTH"}
            >
              <KeyboardArrowRightIcon />
            </IconButton>
            <IconButton onClick={() => handleDateChange("++")}>
              <KeyboardDoubleArrowRightIcon />
            </IconButton>
          </Stack>
        </Grid>
        {deal && (
          <Grid>
            <FinancialsImportWidget
              deal={deal}
              financial_data_vals={financial_data_vals}
            />
          </Grid>
        )}
      </Grid>
      <div style={{ display: "flex", flexDirection: "column" }}>
        <CustomSimpleTable
          columns={getColumns(period, date, deal)}
          initialState={{ pinnedColumns: { left: ["type"] } }}
          rows={getRows(
            financial_data_types,
            financial_data_vals,
            getColumns(period, date, deal),
            category,
          )}
          disableRowSelectionOnClick
          sx={{
            "& .MuiDataGrid-row--borderBottom .MuiDataGrid-columnHeader": {
              borderBottom: "1px solid #000",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: "600",
            },
          }}
        />
      </div>
      <Box sx={{ display: "block", textAlign: "right" }}>
        <Typography variant="caption">
          {subtitle && subtitle + " - "}
        </Typography>
        <InfoButton
          variant="link"
          label="data"
          data={{ financial_data_vals, financial_data_types, deal }}
        />
      </Box>
    </>
  );
};

const DealFinancials = ({ page, data: deal }) => {
  const { nextLastDayOfPeriod } = useDealFinancialsHelpers();
  const [qsState, setQState] = useQueryStringState({
    period: "MONTH",
    category: "ACTUAL",
    date: nextLastDayOfPeriod(new Date(), deal?.closing_month, "YEAR"),
  });

  const { useGetByDealId } = useCustomDataSource(
    "visualization.DealVisualizationInformation",
  );
  const { data, isSuccess } = useGetByDealId({
    dealId: deal.id,
  });

  return (
    <Container maxWidth="100%">
      <DealDataTable
        financial_data_vals={data?.financial_data_vals}
        financial_data_types={data?.financial_data_types}
        deal={data?.deal}
        state={qsState}
        onStateChange={setQState}
      />
    </Container>
  );
};

export default DealFinancials;
